import {
  FINAL_COUNTDOWN_TIME,
  PORTAL_RELOCATE_INTERVAL,
} from '@star-snake/shared'
import { NeonColors, TOP5_COLORS } from './neon-theme'
import { getAppViewport, mountToAppShell } from './appShell'

export interface HUDData {
  leaderboard: { entityId: number; name: string; score: number; isMe: boolean }[]
  timer: number
  maxDuration: number
  myScore: number
  myLength: number
  phase: string
  portalX: number
  portalZ: number
  portalActive: boolean
  cameraX: number
  cameraZ: number
  viewportWidth: number
  viewportHeight: number
  portalDistance: number
  portalTimeLeft: number
}

/** Score label for minimap rendering — entityId, score, color, isMe. */
export interface MinimapScoreLabel {
  entityId: number
  score: number
  color: string
  isMe: boolean
}

export class HUD {
  private container: HTMLDivElement
  private countdownEl: HTMLDivElement
  private portalHEl: HTMLDivElement
  private portalVEl: HTMLDivElement
  private portalDistEl: HTMLSpanElement
  private styleEl: HTMLStyleElement
  private onExitCallback: (() => void) | null = null
  private resizeHandler: () => void

  private lastScore: number = 0
  private lastStarEquiv: number = 0

  /** Top-5 color map: entityId → CSS color string. */
  private top5ColorMap: Map<number, string> = new Map()
  /** Score labels for minimap: top-5 + player entries with color and score. */
  private scoreLabels: MinimapScoreLabel[] = []

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'hud'
    this.container.style.cssText = `
      position: absolute; inset: 0;
      pointer-events: none; z-index: 100;
      font-family: 'Inter', sans-serif;
    `

    this.styleEl = document.createElement('style')
    this.styleEl.textContent = `
      @keyframes countdownPulse {
        0% { transform: translate(-50%, -50%) scale(1); }
        50% { transform: translate(-50%, -50%) scale(1.15); }
        100% { transform: translate(-50%, -50%) scale(1); }
      }
    `
    this.container.appendChild(this.styleEl)

    this.countdownEl = this.createCountdown()
    this.portalHEl = this.createPortalBar()
    this.portalVEl = this.createPortalBar()
    this.portalDistEl = document.createElement('span')
    this.portalDistEl.style.cssText = `
      position: absolute; display: none; pointer-events: none;
      font-size: 10px; font-weight: bold;
      color: ${NeonColors.portal};
      text-shadow: 0 0 4px ${NeonColors.portalGlow};
      white-space: nowrap;
    `

    this.container.appendChild(this.countdownEl)
    this.container.appendChild(this.portalHEl)
    this.container.appendChild(this.portalVEl)
    this.container.appendChild(this.portalDistEl)
    mountToAppShell(this.container)

    this.resizeHandler = () => {}
    window.addEventListener('resize', this.resizeHandler)
  }

  private createCountdown(): HTMLDivElement {
    const el = document.createElement('div')
    el.style.cssText = `
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      font-size: 48px; font-weight: bold;
      color: ${NeonColors.danger};
      display: none; pointer-events: none;
      text-shadow: 0 0 20px ${NeonColors.dangerGlow}, 0 0 40px ${NeonColors.dangerGlow};
      animation: countdownPulse 1s ease-in-out infinite;
    `
    return el
  }

  private createPortalBar(): HTMLDivElement {
    const el = document.createElement('div')
    el.style.cssText = `
      position: absolute; display: none; pointer-events: none;
    `
    return el
  }

  flashStarCollected(): void {
    const flash = document.createElement('div')
    flash.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      box-shadow: inset 0 0 80px ${NeonColors.goldGlow}, inset 0 0 120px ${NeonColors.goldGlow};
      opacity: 1;
      transition: opacity 0.5s ease-out;
    `
    this.container.appendChild(flash)
    requestAnimationFrame(() => {
      flash.style.opacity = '0'
    })
    setTimeout(() => flash.remove(), 600)
  }

  setOnExit(callback: () => void): void {
    this.onExitCallback = callback
  }

  getTop5ColorMap(): Map<number, string> {
    return this.top5ColorMap
  }

  getScoreLabels(): MinimapScoreLabel[] {
    return this.scoreLabels
  }

  update(data: HUDData): void {
    this.updateColorMap(data.leaderboard)

    const remaining = Math.max(0, data.maxDuration - data.timer)
    this.updateWarnings(remaining)
    this.updatePortalStripe(data)
  }

  /**
   * Build top-5 color map with "5 others around me" logic:
   * - If player is outside top 5 (rank >= 5): color ranks 0-4
   * - If player is inside top 5: color 5 others nearest to player's rank
   *   e.g. player at rank 2 → color ranks 0,1,3,4,5
   */
  private updateColorMap(
    entries: { entityId: number; name: string; score: number; isMe: boolean }[]
  ): void {
    this.top5ColorMap.clear()
    this.scoreLabels = []

    const myIdx = entries.findIndex((e) => e.isMe)

    // Determine which 5 non-player entries to highlight
    let highlighted: number[] = []
    if (myIdx < 0 || myIdx >= 5) {
      // Player not in top 5 — highlight first 5
      highlighted = []
      for (let i = 0; i < entries.length && highlighted.length < 5; i++) {
        if (!entries[i].isMe) highlighted.push(i)
      }
    } else {
      // Player in top 5 — highlight 5 entries around player
      // Collect non-player entries above and below
      const above: number[] = []
      const below: number[] = []
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].isMe) continue
        if (i < myIdx) above.push(i)
        else below.push(i)
      }
      // Take from above, then fill from below to get 5
      highlighted = [...above]
      for (const idx of below) {
        if (highlighted.length >= 5) break
        highlighted.push(idx)
      }
      // If not enough below, should already have enough above
      highlighted = highlighted.slice(0, 5)
    }

    // Assign colors
    for (let c = 0; c < highlighted.length; c++) {
      const idx = highlighted[c]
      const entry = entries[idx]
      this.top5ColorMap.set(entry.entityId, TOP5_COLORS[c])
      this.scoreLabels.push({
        entityId: entry.entityId,
        score: entry.score,
        color: TOP5_COLORS[c],
        isMe: false,
      })
    }
  }

  private updateWarnings(remaining: number): void {
    if (remaining <= FINAL_COUNTDOWN_TIME) {
      this.countdownEl.style.display = 'block'
      this.countdownEl.textContent = String(Math.ceil(remaining))
    } else {
      this.countdownEl.style.display = 'none'
    }
  }

  /**
   * Portal indicator: 20px pulsing purple stripe on nearest edge.
   * Near corners the stripe becomes an L-shaped corner piece.
   */
  private updatePortalStripe(data: HUDData): void {
    if (!data.portalActive) {
      this.portalHEl.style.display = 'none'
      this.portalVEl.style.display = 'none'
      this.portalDistEl.style.display = 'none'
      return
    }

    const halfW = data.viewportWidth / 2
    const halfH = data.viewportHeight / 2
    const dx = data.portalX - data.cameraX
    const dz = data.portalZ - data.cameraZ
    if (Math.abs(dx) < halfW && Math.abs(dz) < halfH) {
      this.portalHEl.style.display = 'none'
      this.portalVEl.style.display = 'none'
      this.portalDistEl.style.display = 'none'
      return
    }

    const angle = Math.atan2(dz, dx)
    const viewport = getAppViewport()
    const screenW = viewport.width
    const screenH = viewport.height
    const pulseAlpha = 0.5 + Math.sin(Date.now() * 0.005) * 0.3
    const portalColor = NeonColors.portal
    // Shrink indicator as portal's minute expires: 200% at start → 0% at end
    const timeFraction = Math.max(0, Math.min(1, data.portalTimeLeft / PORTAL_RELOCATE_INTERVAL))
    const scaleFactor = timeFraction * 2
    const W = Math.max(0, Math.round(3 * scaleFactor))
    const L = Math.max(0, Math.round(24 * scaleFactor))
    if (W < 1 || L < 1) {
      this.portalHEl.style.display = 'none'
      this.portalVEl.style.display = 'none'
      this.portalDistEl.style.display = 'none'
      return
    }

    const dist = Math.round(data.portalDistance / 100)
    this.portalDistEl.textContent = String(dist)

    // Project portal direction onto screen edge to find intersection point
    const cosA = Math.cos(angle)
    const sinA = Math.sin(angle)
    const tX = cosA !== 0 ? (screenW / 2) / Math.abs(cosA) : Infinity
    const tY = sinA !== 0 ? (screenH / 2) / Math.abs(sinA) : Infinity
    const t = Math.min(tX, tY)
    const hitX = screenW / 2 + cosA * t
    const hitY = screenH / 2 + sinA * t

    // Determine which edges are hit (corner = both)
    const onRight = hitX >= screenW - 1
    const onLeft = hitX <= 1
    const onBottom = hitY >= screenH - 1
    const onTop = hitY <= 1

    const nearCorner = (onRight || onLeft) && (onTop || onBottom)

    // Reset both bars
    this.portalHEl.style.display = 'none'
    this.portalVEl.style.display = 'none'

    if (nearCorner) {
      const hBar = this.portalHEl
      const vBar = this.portalVEl

      if (onRight && onTop) {
        hBar.style.cssText = `position:absolute;display:block;pointer-events:none;top:0;right:0;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        vBar.style.cssText = `position:absolute;display:block;pointer-events:none;top:0;right:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(screenW - L - 14, W + 4)
      } else if (onLeft && onTop) {
        hBar.style.cssText = `position:absolute;display:block;pointer-events:none;top:0;left:0;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        vBar.style.cssText = `position:absolute;display:block;pointer-events:none;top:0;left:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(L + 4, W + 4)
      } else if (onRight && onBottom) {
        hBar.style.cssText = `position:absolute;display:block;pointer-events:none;bottom:0;right:0;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        vBar.style.cssText = `position:absolute;display:block;pointer-events:none;bottom:0;right:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(screenW - L - 14, screenH - W - 18)
      } else {
        hBar.style.cssText = `position:absolute;display:block;pointer-events:none;bottom:0;left:0;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        vBar.style.cssText = `position:absolute;display:block;pointer-events:none;bottom:0;left:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(L + 4, screenH - W - 18)
      }
    } else {
      const bar = this.portalHEl
      if (onRight) {
        const cy = Math.max(L / 2, Math.min(screenH - L / 2, hitY))
        bar.style.cssText = `position:absolute;display:block;pointer-events:none;top:${cy - L / 2}px;right:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(screenW - W - 18, cy - 6)
      } else if (onLeft) {
        const cy = Math.max(L / 2, Math.min(screenH - L / 2, hitY))
        bar.style.cssText = `position:absolute;display:block;pointer-events:none;top:${cy - L / 2}px;left:0;width:${W}px;height:${L}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(W + 4, cy - 6)
      } else if (onBottom) {
        const cx = Math.max(L / 2, Math.min(screenW - L / 2, hitX))
        bar.style.cssText = `position:absolute;display:block;pointer-events:none;bottom:0;left:${cx - L / 2}px;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(cx - 6, screenH - W - 18)
      } else {
        const cx = Math.max(L / 2, Math.min(screenW - L / 2, hitX))
        bar.style.cssText = `position:absolute;display:block;pointer-events:none;top:0;left:${cx - L / 2}px;width:${L}px;height:${W}px;background:${portalColor};opacity:${pulseAlpha};`
        this.positionDistLabel(cx - 6, W + 4)
      }
    }
  }

  private positionDistLabel(x: number, y: number): void {
    this.portalDistEl.style.cssText = `
      position: absolute; display: block; pointer-events: none;
      left: ${x}px; top: ${y}px;
      font-size: 10px; font-weight: bold;
      color: ${NeonColors.portal};
      text-shadow: 0 0 4px ${NeonColors.portalGlow};
      white-space: nowrap;
    `
  }

  dispose(): void {
    window.removeEventListener('resize', this.resizeHandler)
    this.container.remove()
  }
}
